import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeEvery, put, all, select } from 'redux-saga/effects';

import { ITableConfig } from '../components/common/table';

import Api from '../api';

import { IRootState } from './index';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';

import { selectors as authSelectors } from '../reducers/Auth';
import PagedResultSet from '../models/common/PagedResultSet';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import ProjectStaff from 'src/models/ProjectStaff';

// Store
export interface IProjectStaffingState {
    isAdministrationLoading: boolean;
    isStaffingLoading: boolean;
    projectStaffingTableConfig: ITableConfig;
    projectAdminTableConfig: ITableConfig;
    staffing: PagedResultSet<ProjectStaff> | undefined;
    projectAdminStaffing: PagedResultSet<ProjectAdministratorStaff> | undefined;
}

const initialState: IProjectStaffingState = {
    isAdministrationLoading: false,
    isStaffingLoading: false,
    staffing: undefined,
    projectAdminStaffing: undefined,
    projectAdminTableConfig: {
        pageNumber: 1,
        rowCount: 10,
        idField: 'id',
        columns: [
            { name: 'Last Name', mapping: 'user.lastName' },
            { name: 'First Name', mapping: 'user.firstName' },
            { name: 'Role', mapping: 'projectAdministrationType.name' },
            { name: 'Start Date', mapping: 'startDate' },
            { name: 'End Date', mapping: 'endDate' },
        ],
    },
    projectStaffingTableConfig: {
        pageNumber: 1,
        rowCount: 10,
        idField: 'id',
        columns: [
            { name: 'Last Name', mapping: 'user.lastName' },
            { name: 'First Name', mapping: 'user.firstName' },
            { name: 'Start Date', mapping: 'startDate' },
            { name: 'End Date', mapping: 'endDate' },
        ],
    },
};

// Action Types
export enum actionTypes {
    GET_PROJECT_STAFFING = '[project_staffing] GET_PROJECT_STAFFING',
    GET_PROJECT_STAFFING_SUCCESS = '[project_staffing] GET_PROJECT_STAFFING_SUCCESS',
    GET_PROJECT_ADMINISTRATION = '[project_staffing] GET_PROJECT_ADMINISTRATION',
    GET_PROJECT_ADMINISTRATION_SUCCESS = '[project_staffing] GET_PROJECT_ADMINISTRATION_SUCCESS',
}

// Action Creator
export const actionCreators = {
    // TODO: multiple ODataOptions
    getProjectStaffing: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_PROJECT_STAFFING, payload: { clientNumber: clientNumber, projectNumber: projectNumber } };
    },
    // TODO: multiple PagedResultSets
    getProjectStaffingSuccess: (users: PagedResultSet<ProjectStaff>) => {
        return { type: actionTypes.GET_PROJECT_STAFFING_SUCCESS, payload: users };
    },
    getProjectAdmininistration: (clientNumber: number, projectNumber: number) => {
        return { type: actionTypes.GET_PROJECT_ADMINISTRATION, payload: { clientNumber: clientNumber, projectNumber: projectNumber } };
    },
    // TODO: multiple PagedResultSets
    getProjectAdministrationSuccess: (users: PagedResultSet<ProjectAdministratorStaff>) => {
        return { type: actionTypes.GET_PROJECT_ADMINISTRATION_SUCCESS, payload: users };
    },
};

// Reducer
const reducer: Reducer<IProjectStaffingState> = (state: IProjectStaffingState = initialState, action: Action): IProjectStaffingState => {
    switch (action.type) {
        case actionTypes.GET_PROJECT_STAFFING: {
            return {
                ...state,
                isStaffingLoading: true,
                staffing: initialState.staffing,
            };
        }
        case actionTypes.GET_PROJECT_STAFFING_SUCCESS: {
            const getStaffingPayload = action as { type: string; payload: PagedResultSet<ProjectStaff> };
            return {
                ...state,
                isStaffingLoading: false,
                staffing: getStaffingPayload.payload,
            };
        }
        case actionTypes.GET_PROJECT_ADMINISTRATION: {
            return {
                ...state,
                isStaffingLoading: true,
                projectAdminStaffing: initialState.projectAdminStaffing,
            };
        }
        case actionTypes.GET_PROJECT_ADMINISTRATION_SUCCESS: {
            const getStaffingPayload = action as { type: string; payload: PagedResultSet<ProjectAdministratorStaff> };
            return {
                ...state,
                isStaffingLoading: false,
                projectAdminStaffing: getStaffingPayload.payload,
            };
        }
        default:
            return state;
    }
};

export default reducer;

export const selectors = {
    isStaffingLoading: (state: IRootState): boolean => state.projectStaffing.isStaffingLoading && state.layout.initialLoading,
    isAdministrationLoading: (state: IRootState): boolean => state.projectStaffing.isAdministrationLoading && state.layout.initialLoading,
    staffing: (state: IRootState): PagedResultSet<ProjectStaff> | undefined => state.projectStaffing.staffing,
    projectAdminStaffing: (state: IRootState): PagedResultSet<ProjectAdministratorStaff> | undefined => state.projectStaffing.projectAdminStaffing,
    projectStaffingTableConfig: (state: IRootState) => state.projectStaffing.projectStaffingTableConfig,
    projectAdminTableConfig: (state: IRootState) => state.projectStaffing.projectAdminTableConfig,
};

// Side-Effects
const sagas = {
    getProjectStaffing: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Project.getStaffing, action.payload.clientNumber, action.payload.projectNumber, jwt), delay(200)]);
        yield put(actionCreators.getProjectStaffingSuccess(result));
    },

    getProjectAdminStaffing: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Project.getAdministration, action.payload.clientNumber, action.payload.projectNumber, jwt), delay(200)]);
        yield put(actionCreators.getProjectAdministrationSuccess(result));
    },
};

const watches = {
    getProjectStaffing: function* () {
        yield takeEvery(actionTypes.GET_PROJECT_STAFFING, sagas.getProjectStaffing);
    },
    getProjectAdminStaffing: function* () {
        yield takeEvery(actionTypes.GET_PROJECT_ADMINISTRATION, sagas.getProjectAdminStaffing);
    },
};

export function* saga() {
    yield fork(watches.getProjectStaffing);
    yield fork(watches.getProjectAdminStaffing);
}



// WEBPACK FOOTER //
// ./src/reducers/ProjectStaffing.ts